/**
 * Pure business logic for Companies module
 * ตรรกะธุรกิจสำหรับโมดูลบริษัท (Pure functions เท่านั้น)
 */

import type { Company, CompanyProject, UpdateCompanyInput, FormalField } from './types'
import { FORMAL_FIELDS, ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from './constants'

/**
 * Check if a field is a formal field
 * ตรวจสอบว่าฟิลด์เป็นฟิลด์ทางการหรือไม่
 */
export function isFormalField(field: string): field is FormalField {
  return FORMAL_FIELDS.includes(field as FormalField)
}

/**
 * Check if update requires evidence
 * ตรวจสอบว่าการอัปเดตต้องมีหลักฐานหรือไม่
 */
export function requiresEvidence(changes: Partial<UpdateCompanyInput>): boolean {
  return Object.keys(changes).some((key) => isFormalField(key))
}

/**
 * Get list of formal fields being changed
 * รายการฟิลด์ทางการที่กำลังเปลี่ยนแปลง
 */
export function getChangedFormalFields(changes: Partial<UpdateCompanyInput>): FormalField[] {
  return Object.keys(changes).filter((key) => isFormalField(key))
}

/**
 * Validation result type
 */
export interface ValidationResult {
  valid: boolean
  errors: Record<string, string>
}

/**
 * Validate company form input
 * ตรวจสอบความถูกต้องของข้อมูลบริษัท
 */
export function validateCompanyInput(
  input: Partial<UpdateCompanyInput>,
  isRequired = false
): ValidationResult {
  const errors: Record<string, string> = {}

  // Required fields validation (only when creating)
  if (isRequired) {
    if (!input.nameTh?.trim()) {
      errors['nameTh'] = 'companies.errors.required'
    }

    if (!input.nameEn?.trim()) {
      errors['nameEn'] = 'companies.errors.required'
    }

    if (!input.taxId?.trim()) {
      errors['taxId'] = 'companies.errors.required'
    }

    if (!input.registrationNo?.trim()) {
      errors['registrationNo'] = 'companies.errors.required'
    }

    if (!input.registeredCapital?.trim()) {
      errors['registeredCapital'] = 'companies.errors.required'
    }

    if (!input.businessType?.trim()) {
      errors['businessType'] = 'companies.errors.required'
    }

    if (!input.address?.trim()) {
      errors['address'] = 'companies.errors.required'
    }
  }

  // Tax ID format validation (13 digits)
  if (input.taxId && !/^\d{13}$/.test(input.taxId.replace(/-/g, ''))) {
    errors['taxId'] = 'companies.errors.taxIdFormat'
  }

  // Email validation (if provided)
  if (input.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
    errors['email'] = 'companies.errors.emailFormat'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate company creation input
 * ตรวจสอบความถูกต้องของข้อมูลสร้างบริษัท
 */
export function validateCreateCompanyInput(
  input: Partial<{
    nameTh: string
    nameEn: string
    taxId: string
    registrationNo: string
    registeredCapital: string
    businessType: string
    directors: string
    address: string
    phone: string
    email: string
    contactPerson: string
    contactPhone: string
    evidenceFile?: File
  }>
): ValidationResult {
  return validateCompanyInput(input, true)
}

/**
 * Format tax ID with hyphens (X-XXXX-XXXXX-XX-X)
 * จัดรูปแบบเลขประจำตัวผู้เสียภาษี
 */
export function formatTaxId(taxId: string): string {
  const cleaned = taxId.replace(/\D/g, '')
  if (cleaned.length !== 13) return taxId

  return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 5)}-${cleaned.slice(5, 10)}-${cleaned.slice(10, 12)}-${cleaned.slice(12)}`
}

/**
 * Clean tax ID (remove hyphens)
 * ล้างเลขประจำตัวผู้เสียภาษี (เอา hyphen ออก)
 */
export function cleanTaxId(taxId: string): string {
  return taxId.replace(/-/g, '')
}

/**
 * Format registered capital with commas
 * จัดรูปแบบทุนจดทะเบียน
 */
export function formatCapital(capital: string): string {
  const num = parseFloat(capital.replace(/,/g, ''))
  if (isNaN(num)) return capital
  return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Parse status from company projects
 * กำหนดสถานะจากโครงการของบริษัท
 */
export function deriveCompanyStatus(
  projects: CompanyProject[]
): 'in-progress' | 'completed' | 'not-started' {
  if (projects.length === 0) return 'not-started'

  const hasInProgress = projects.some((p) => p.status === 'in-progress')
  if (hasInProgress) return 'in-progress'

  return 'completed'
}

/**
 * Filter companies based on search and status
 * กรองบริษัทตามคำค้นหาและสถานะ
 */
export function filterCompanies(companies: Company[], search: string, status: string): Company[] {
  return companies.filter((company) => {
    // Search filter
    if (search.trim()) {
      const query = search.toLowerCase()
      const matchesSearch =
        company.nameTh.toLowerCase().includes(query) ||
        company.nameEn.toLowerCase().includes(query) ||
        company.taxId.includes(query)
      if (!matchesSearch) return false
    }

    // Status filter
    if (status !== 'all' && company.status !== status) {
      return false
    }

    return true
  })
}

/**
 * Sort companies by latest activity
 * เรียงบริษัทตามกิจกรรมล่าสุด
 */
export function sortByLatestActivity(companies: Company[]): Company[] {
  return [...companies].sort((a, b) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })
}

/**
 * File validation result
 */
export interface FileValidationResult {
  valid: boolean
  error?: string
}

/**
 * Validate file for upload
 * ตรวจสอบไฟล์สำหรับอัปโหลด
 */
export function validateFile(file: File): FileValidationResult {
  // Check file type
  if (!ALLOWED_FILE_TYPES.includes(file.type as (typeof ALLOWED_FILE_TYPES)[number])) {
    return { valid: false, error: 'companies.errors.fileType' }
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'companies.errors.fileSize' }
  }

  return { valid: true }
}

/**
 * Get file extension
 * ดึงนามสกุลไฟล์
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}

/**
 * Format file size to human readable
 * จัดรูปแบบขนาดไฟล์ให้อ่านง่าย
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${String(parseFloat((bytes / Math.pow(k, i)).toFixed(2)))} ${sizes[i] ?? ''}`
}

/**
 * Get company display name
 * รับชื่อบริษัทสำหรับแสดงผล
 */
export function getCompanyDisplayName(company: Pick<Company, 'nameTh' | 'nameEn'>): string {
  return company.nameTh || company.nameEn
}

/**
 * Get company full display (TH + EN)
 * รับชื่อบริษัทแบบเต็ม (ไทย + อังกฤษ)
 */
export function getCompanyFullDisplay(company: Pick<Company, 'nameTh' | 'nameEn'>): string {
  if (company.nameTh && company.nameEn) {
    return `${company.nameTh} (${company.nameEn})`
  }
  return company.nameTh || company.nameEn || ''
}

/**
 * Paginate array of items
 * แบ่งหน้ารายการ
 */
export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  if (page < 1) return []
  if (pageSize < 1) return []
  const start = (page - 1) * pageSize
  return items.slice(start, start + pageSize)
}

/**
 * Calculate total pages
 * คำนวณจำนวนหน้าทั้งหมด
 */
export function calculateTotalPages(totalItems: number, pageSize: number): number {
  if (totalItems === 0) return 1
  if (pageSize < 1) return 1
  return Math.ceil(totalItems / pageSize)
}

/**
 * Get page numbers for pagination display
 * รายการหมายเลขหน้าสำหรับแสดง
 */
export function getPageNumbers(currentPage: number, totalPages: number, maxVisible = 5): number[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const half = Math.floor(maxVisible / 2)
  let start = currentPage - half
  let end = currentPage + half

  if (start < 1) {
    start = 1
    end = maxVisible
  }

  if (end > totalPages) {
    end = totalPages
    start = totalPages - maxVisible + 1
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

/**
 * Check if company has active projects
 * ตรวจสอบว่าบริษัทมีแฟ้มงานที่กำลังดำเนินการหรือไม่
 */
export function hasActiveProjects(projects: CompanyProject[]): boolean {
  return projects.some((p) => p.status === 'in-progress')
}

/**
 * Count projects by status
 * นับจำนวนแฟ้มงานตามสถานะ
 */
export function countProjectsByStatus(projects: CompanyProject[]): Record<string, number> {
  return projects.reduce<Record<string, number>>((acc, project) => {
    acc[project.status] = (acc[project.status] ?? 0) + 1
    return acc
  }, {})
}
